<script setup lang="ts">
import BooleanIcon from '@/components/shared/BooleanIcon.vue';
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog.vue';
import ButtonContent from '@/components/devextreme/ButtonContent.vue';
import LocationEditPopup from '@/components/maintenance/LocationEditPopup.vue';
import { useMaintenanceDelete } from '@/composables/useMaintenanceDelete';
import { RoleGroups } from '@/constants/Roles';
import { removedMessage } from '@/helpers/notificationMessages';
import { t } from '@/i18n';
import { useLocationService, dataSource } from '@/services/api/locationService';
import type { ILocationResponse } from '@/types/interfaces/MaintenanceTypes';
import Icons from '@/constants/Icons';
import DxButton from 'devextreme-vue/button';
import { DxColumn, DxDataGrid, DxItem, DxToolbar } from 'devextreme-vue/data-grid';
import { ref, shallowRef } from 'vue';

definePage({
  name: 'MaintenanceLocations',
  meta: {
    title: 'maintenance.tabs.locations',
    roles: RoleGroups.Maintenance
  }
});

const locationsGridRef = shallowRef<DxDataGrid>();
const locationEditPopupRef = ref<typeof LocationEditPopup>();
const deleteDialogRef = ref<InstanceType<typeof DeleteConfirmationDialog>>();
const api = useLocationService();

async function confirmDelete(data: ILocationResponse): Promise<void> {
  const key = {
    id: data.id,
    name: data.name,
    stationCode: data.stationCode
  };

  await deleteDialogRef?.value?.show(key, `${key.stationCode} - ${key.name}`);
}

const deleteLocation = useMaintenanceDelete({
  dataSource,
  dialog: () => deleteDialogRef.value,
  remove: ({ id }: { id: number, name: string, stationCode: string }) => api.deleteLocation(id),
  key: ({ id }) => id,
  removedMessage: ({ name, stationCode }) => removedMessage(t('entities.location'), name, stationCode.trim().toUpperCase())
});

async function updateData(data: ILocationResponse, key?: number | null): Promise<void> {
  dataSource.upsert(data, key);
}
</script>

<template>
  <LocationEditPopup ref="locationEditPopupRef" @update="updateData" />
  <DeleteConfirmationDialog ref="deleteDialogRef" :entity="$t('entities.location')"
      @confirm="deleteLocation" />

  <DxDataGrid id="locationsGrid" ref="locationsGridRef" :data-source="dataSource">
    <DxToolbar>
      <DxItem location="before" template="title-template" />
      <DxItem location="before" template="add-button-template" />
      <DxItem location="after" template="refresh-button-template" />
      <DxItem :locate-in-menu="'auto'" location="after" name="searchPanel" />
    </DxToolbar>

    <DxColumn :sort-index="0" :sort-order="'asc'" :caption="$t('labels.station')" data-field="stationCode" width="150px" />
    <DxColumn :sort-index="1" :sort-order="'asc'" data-field="name" />
    <DxColumn data-field="driveTimeMinutes" width="200px" />
    <DxColumn cell-template="boolean-cell-template" data-field="enabled" :false-text="$t('common.no')"
        :true-text="$t('common.yes')" width="150px" />
    <DxColumn :visible="false" type="adaptive" />
    <DxColumn :alignment="'center'" :allow-filtering="false" :allow-sorting="false" caption="" cell-template="command-cell-template"
        data-field="id" name="command-column" width="100px" />

    <template #title-template>
      <span class="h3 fw-semibold">{{ $t('maintenance.maintain', { entities: $t('maintenance.tabs.locations') }) }}</span>
    </template>

    <template #add-button-template>
      <DxButton class="ms-3" type="success" @click="locationEditPopupRef?.addLocation()">
        <ButtonContent :icon="Icons.Plus" :text="$t('common.addEntity', { entity: $t('entities.location') })" />
      </DxButton>
    </template>

    <template #refresh-button-template>
      <DxButton type="primary" @click="locationsGridRef?.instance?.refresh()">
        <ButtonContent :icon="Icons.ArrowsRotate" />
      </DxButton>
    </template>

    <template #boolean-cell-template="{ data }">
      <BooleanIcon :value="data.value" />
    </template>

    <template #command-cell-template="{ data }">
      <DxButton type="primary" @click="locationEditPopupRef?.editLocation(data.value)">
        <ButtonContent :icon="Icons.Pencil" />
      </DxButton>
      <DxButton type="danger" @click="confirmDelete(data.data)">
        <ButtonContent :icon="Icons.Trash" />
      </DxButton>
    </template>
  </DxDataGrid>
</template>

<style scoped>

</style>